import Vue from 'vue';
import moment from 'moment';
import {
  Module, VuexModule, Mutation, Action,
} from 'vuex-module-decorators';
import API from '@/lib/API';
import { SOCKET_LEAD_UPDATED } from '@/store/actions/leads';
import {
  Lead, LeadUpdateType, SocketLeadUpdatedData, LeadType, ClosedReasonType,
  ProjectsPool,
  ProjectsInfoPool,
  Task,
  ProjectInfo,
  Attachment,
  AssignmentAssignee,
} from '@/types';
// import {
//   FATHER_ATTACHMENT_TYPES,
//   ELETTRICA_FIGLIA_ATTACHMENT_TYPES,
//   TERMICA_FIGLIA_ATTACHMENT_TYPES,
//   SHARED_CHILD_ATTACHMENT_TYPES,
// } from '@/lib/AttachmentTypes';

moment.locale('it');

@Module
export default class Leads extends VuexModule {
  // State

  // Pool dei progetti mostrati
  // (include i progetti figli a cui l'utente ha accesso)
  pool: ProjectsPool = {}
  poolUpdating: {
    [key: number]: boolean;
  } = {}
  poolDirty: {
    [key: number]: boolean;
  } = {}

  // Progetto selezionato
  project: number | null = null
  projectUpdating = false
  projectDirty = false
  // Elenco degli Id delle figlie del progetto selezionato
  projectChildren: number[] = []

  // Pool che contiene informazioni limitate sulle lead
  // (id, status, allowedUsers)
  infoPool: ProjectsInfoPool = {}

  // Aggiornamento dei progetti in corso
  projectsUpdating = false

  // Ritorna il pool con tutti i progetti mostrati e
  // utilizzati (include i progetti figli)
  get currentPool(): ProjectsPool {
    return this.pool;
  }

  // Ritorna il pool che contiene informazioni limitate sui progetti
  // (id, status, allowedUsers)
  get currentInfoPool(): ProjectsInfoPool {
    return this.infoPool;
  }

  // Ritorna il progetto selezionato
  get currentLead(): Lead | null {
    return this.project !== null && this.project in this.pool
      ? this.pool[this.project] : null;
  }

  // Ritorna i figli del progetto selezionato
  get currentChildren(): ProjectsPool {
    const result: ProjectsPool = {};

    this.projectChildren.forEach((id) => {
      if (id in this.pool) result[id] = this.pool[id];
    });

    return result;
  }

  // Ritorna i figli del progetto selezionato
  get currentChildrenInfo(): ProjectsInfoPool {
    const result: ProjectsInfoPool = {};

    this.projectChildren.forEach((id) => {
      if (id in this.infoPool) result[id] = this.infoPool[id];
    });

    return result;
  }

  get isProjectUpdating(): boolean { return this.projectUpdating; }

  get isProjectDirty(): boolean { return this.projectDirty; }

  get currentFather(): Lead | null {
    if (!this.project || !this.currentLead) return null;

    // Se è una figlia...
    if (['installatore-figlia', 'elettrica-figlia', 'termica-figlia'].includes(this.currentLead.type)) {
      // Recupera l'id del padre
      if (this.currentLead.Tasks.length > 0) {
        const { fatherLeadId } = this.currentLead.Tasks[0];

        return fatherLeadId && fatherLeadId in this.pool
          ? this.pool[fatherLeadId]
          : null;
      }

      return null;
    }

    return this.currentLead;
  }

  // Ritorna lo stato dei pagamenti della commessa a cui appartiene la
  // Lead selezionata (sia che essa sia il padre che la figlia).
  get currentLeadPayments(): number {
    if (!this.project || !this.currentLead) return 0;

    // Se è una figlia...
    if (['installatore-figlia', 'elettrica-figlia', 'termica-figlia'].includes(this.currentLead.type)) {
      // Recupera l'id del padre
      if (this.currentLead.Tasks.length > 0) {
        const { fatherLeadId } = this.currentLead.Tasks[0];

        return fatherLeadId && fatherLeadId in this.pool
          ? this.pool[fatherLeadId].payments
          : 0;
      }

      return 0;
    }

    return this.currentLead.payments;
  }

  // Ritorna l'elenco di tutti gli allegati della Lead selezionata includendo
  // anche tutti quelli delle eventuali figlie.
  get currentLeadAttachments(): Attachment[] {
    if (!this.project || !this.currentLead) return [];

    const attachments: Attachment[] = [];

    if (this.currentLead) attachments.push(...this.currentLead.Attachments);

    // Se è una figlia...
    if (['installatore-figlia', 'elettrica-figlia', 'termica-figlia'].includes(this.currentLead.type)) {
      // ... aggiunge anche gli allegati del padre di un determinato tipo
      if (this.currentFather) {
        // TEMPORANEO: Usa solo i 3 tipi della versione precedente per mantenere la compatibilità
        // TODO: Sostituire con la logica completa basata su AttachmentTypes
        const allowedParentAttachmentTypes: string[] = [
          'endOfWork', 'titoloAutorizzativo', 'autodichiarazioneCliente', 'ultimaBollettaElettrica', 'visuraCatastale', 'planimetriaCatastale', 'pratiche',
        ];

        attachments.push(
          ...(
            this.currentFather.Attachments
              .filter((attachment) => allowedParentAttachmentTypes.includes(attachment.fieldname))
          ),
        );
      }
    } else {
      // La Lead è un potenziale padre

      // Aggiunge gli allegati delle eventuali figlie di un determinato tipo
      const childrenToUse = Object.keys(this.currentChildren).length > 0 ? this.currentChildren : this.currentChildrenInfo;
      Object.values(childrenToUse).forEach((child) => {
        attachments.push(
          ...child.Attachments,
          // .filter((attachment) => ['fotoImpianto', 'fotoDG', 'fotoInverter', 'fotoBatteria'].includes(attachment.fieldname))
        );
      });
    }

    // Ritorna l'elenco degli allegati ordinati per `createdAt`
    attachments.sort((a, b) => {
      if (a.createdAt < b.createdAt) return -1;
      if (a.createdAt > b.createdAt) return 1;
      return 0;
    });

    return attachments;
  }

  get currentElettricaFiglia(): Lead | null {
    // escludo anche il caso che la Lead sia una figlia
    if (!this.project
      || !this.currentLead
      || ['installatore-figlia', 'elettrica-figlia', 'termica-figlia'].includes(this.currentLead.type)) return null;

    const childLeads: Lead[] = [];
    Object.values(this.currentChildren).forEach((child) => {
      if (child.type === 'elettrica-figlia' && child.status !== 'closed' && child.status !== 'rejected') {
        childLeads.push(child);
      }
    });

    return childLeads.length > 0 ? childLeads[0] : null;
  }

  get currentTermicaFiglia(): Lead | null {
    // escludo anche il caso che la Lead sia una figlia
    if (!this.project
      || !this.currentLead
      || ['installatore-figlia', 'elettrica-figlia', 'termica-figlia'].includes(this.currentLead.type)) return null;

    const childLeads: Lead[] = [];
    Object.values(this.currentChildren).forEach((child) => {
      if (child.type === 'termica-figlia' && child.status !== 'closed' && child.status !== 'rejected') {
        childLeads.push(child);
      }
    });

    return childLeads.length > 0 ? childLeads[0] : null;
  }

  @Mutation // rev 1
  setUpdating() { this.projectUpdating = true; }

  @Mutation // rev 1
  unsetUpdating() { this.projectUpdating = false; }

  @Mutation // rev 1
  setProjectsUpdating() { this.projectsUpdating = true; }

  @Mutation // rev 1
  unsetProjectsUpdating() { this.projectsUpdating = false; }

  @Mutation // rev 1
  setDirty() { this.projectDirty = true; }

  @Mutation // rev 1
  unsetDirty() { this.projectDirty = false; }

  @Mutation // rev 1
  setLead({ lead }: { lead: Lead }) {
    // Seleziona il progetto attuale
    this.project = lead.id;

    // Lo aggiunge/aggiorna nel pool
    Vue.set(this.pool, lead.id, lead);

    // Aggiunge/aggiorna anche il progetto nell'infoPool
    // con eventuali informazioni aggiorante.
    Vue.set(this.infoPool, lead.id, {
      id: lead.id,
      status: lead.status,
      allowedUsers: lead.allowedUsers,
    });

    // Il progetto non è in aggiornamento, è pulito ed è completo.
    Vue.set(this.poolUpdating, lead.id, false);
    Vue.set(this.poolDirty, lead.id, false);
  }

  @Mutation
  unsetLead() { // rev 1
    // Deseleziona il progetto attuale ma lo lascia nel pool
    this.project = null;
  }

  @Mutation
  addProjectToPool({ project }: { project: Lead }) { // rev 1
    // Aggiunge/aggiorna il progetto nel pool
    Vue.set(this.pool, project.id, project);
    Vue.set(this.poolUpdating, project.id, false);
    Vue.set(this.poolDirty, project.id, false);

    // Lo aggiunge/aggiorna nell'infoPool
    Vue.set(this.infoPool, project.id, {
      id: project.id,
      status: project.status,
      allowedUsers: project.allowedUsers,
    });
  }

  @Mutation
  addProjectsToInfoPool({ projects }: { projects: ProjectInfo[] }) { // rev 1
    // Inserisce i progetti nel pool
    projects.forEach((p) => {
      // Lo aggiunge/aggiorna nell'infoPool
      Vue.set(this.infoPool, p.id, p);
    });
  }

  @Mutation
  addProjectsToPool({ projects }: { projects: Lead[] }) { // rev 1
    // Inserisce i progetti nel pool
    projects.forEach((p) => {
      Vue.set(this.pool, p.id, p);
      Vue.set(this.poolUpdating, p.id, false);
      Vue.set(this.poolDirty, p.id, false);

      // Lo aggiunge/aggiorna nell'infoPool
      Vue.set(this.infoPool, p.id, {
        id: p.id,
        status: p.status,
        allowedUsers: p.allowedUsers,
      });
    });
  }

  @Mutation
  removeProjectFromPool({ id }: { id: number }) { // rev 1
    console.log('mutations:removeProjectFromPool'); // eslint-disable-line no-console
    // Rimuove il progetto dalnel pool
    Vue.delete(this.pool, id);
    Vue.delete(this.poolUpdating, id);
    Vue.delete(this.poolDirty, id);

    // Rimuove il progetto dall'infoPool
    Vue.delete(this.infoPool, id);
  }

  @Action({ rawError: true })
  async updateChildrenList() {
    if (this.project && this.currentLead) {
      // Se è una figlia...
      if (['installatore-figlia', 'elettrica-figlia', 'termica-figlia'].includes(this.currentLead.type)) {
        console.log('store/leads - actions:updateChildrenList() la lead selezionata è una figlia'); // eslint-disable-line no-console

        // ... inserisce in elenco le sorelle e il padre

        // Recupera l'id del padre
        if (this.currentLead.Tasks.length > 0) {
          const { fatherLeadId } = this.currentLead.Tasks[0];

          // se il padre non è già nel pool
          if (fatherLeadId && !this.projectChildren.includes(fatherLeadId)) {
            // lo recupera e lo aggiunge
            const fatherLead = await API.getLead(fatherLeadId);
            this.context.commit('addProjectToPool', { project: fatherLead });
          }
        }

        // Recupera le sorelle ottenendole dal padre (dal pool)
        if (this.currentFather) {
          this.currentFather.Tasks.forEach((task) => {
            // Se il task punta a una lead figlia e questa non è ancora stata scaricata...
            if (task.relationship === 'father' && task.childLeadId
              && !this.projectChildren.includes(task.childLeadId)) {
              // ... recupera la lead figlia e la aggiunge all'elenco
              this.projectChildren.push(task.childLeadId);
            }
          });
        }
      } else {
        console.log('store/leads - actions:updateChildrenList() la lead selezionata NON è una figlia'); // eslint-disable-line no-console

        // È un (possibile) padre

        // Inserisce tutte le figlie
        this.currentLead.Tasks.forEach((task) => {
          // Se il task punta a una lead figlia e questa non è ancora stata scaricata...
          if (task.relationship === 'father' && task.childLeadId
            && !this.projectChildren.includes(task.childLeadId)) {
            // ... recupera la lead figlia e la aggiunge all'elenco
            this.projectChildren.push(task.childLeadId);
          }
        });
      }
    }
  }

  @Action({ rawError: true })
  async getChildren() {
    console.log('store/leads - actions:getChildren'); // eslint-disable-line no-console

    const childrenToGet: number[] = [];

    // Scorre i task di tutti i progetti nel pool
    Object.values(this.pool).forEach((project) => {
      // eslint-disable-next-line max-len
      // console.log(`actions:getChildren project.id = ${project.id}`); // eslint-disable-line no-console

      project.Tasks.forEach((task) => {
        // eslint-disable-next-line max-len
        // console.log(`actions:getChildren task.relationship = ${task.relationship}`); // eslint-disable-line no-console
        // Se il task punta a una lead figlia e questa non è ancora stata scaricata...
        if (task.relationship === 'father' && task.childLeadId
          && task.childLeadId in this.pool === false
          && !childrenToGet.includes(task.childLeadId)) {
          // ... recupera la lead figlia e la aggiunge all'elenco
          // eslint-disable-next-line max-len
          // console.log(`actions:getChildren aggiungo ${task.childLeadId} a childrenToGet`); // eslint-disable-line no-console
          childrenToGet.push(task.childLeadId);
        }
      });
    });

    console.log('store/leads - actions:getChildren childrenToGet =', childrenToGet); // eslint-disable-line no-console

    if (childrenToGet.length === 0) return Promise.resolve();

    // Richiede un elenco di lead figlie e passa l'id della lead padre (per verificare i permessi)
    const params = {
      fatherLeadId: this.project,
      leads: childrenToGet,
    } as {
      fatherLeadId?: number;
      leads?: number[];
    };

    const result = await API.getFullLeads(params);

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    const { leads, leadsInfo } = result;

    if (leadsInfo && leadsInfo.length > 0) {
      this.context.commit('addProjectsToInfoPool', { projects: leadsInfo });
    }

    if (leads.length === 0) {
      return Promise.resolve();
    }

    this.context.commit('addProjectsToPool', { projects: leads });

    return Promise.resolve();
  }

  @Action({ rawError: true })
  async selectLead(
    { id, noChildren = false }: { id: number; noChildren: boolean },
  ): Promise<Lead | Error> {
    console.log(`store/leads - actions:selectLead(${id})`); // eslint-disable-line no-console

    if (!id) Promise.reject(new Error('id non specificato'));

    this.context.commit('setUpdating');

    const result: Lead | Error = await API.getLead(id);

    if (result instanceof Error) {
      this.context.commit('unsetUpdating');
      this.context.dispatch('unselectLead');

      return Promise.reject(result);
    }

    this.context.commit('setLead', { lead: result });

    if (!noChildren) {
      // Aggiorna l'elenco dei progetti figli associati
      await this.context.dispatch('updateChildrenList');

      // Avvia il recupero differito di eventuali progetti figli
      this.context.dispatch('getChildren');
    }

    this.context.commit('unsetDirty');
    this.context.commit('unsetUpdating');

    return Promise.resolve(result);
  }

  @Action({ rawError: true })
  async updateProject(
    { id, noChildren = false }: { id: number; noChildren: boolean },
  ) {
    console.log('store/leads actions:updateProject'); // eslint-disable-line no-console

    if (!id) Promise.reject(new Error('id non specificato'));

    // Definisce il progetto come in aggiornamento
    Vue.set(this.poolUpdating, id, true);

    // Scarica la versione completa del progetto
    console.log(`store/leads actions:updateProject API.getLead(${id})`); // eslint-disable-line no-console
    const result = await API.getLead(id);

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    // Aggiunge/aggiorna il progetto nel pool
    this.context.commit('addProjectToPool', { project: result });

    if (!noChildren) {
      // Aggiorna l'elenco dei progetti figli associati
      await this.context.dispatch('updateChildrenList');
    }

    return Promise.resolve(result);
  }

  @Action({ rawError: true }) // rev 1
  async refreshLead() {
    console.log(`store/leads actions:refreshLead this.project = ${this.project}`); // eslint-disable-line no-console

    // Se non c'è un progetto selezionato si ferma
    if (!this.project) return Promise.reject();

    this.context.commit('setUpdating');

    console.log(`store/leads actions:refreshLead API.getLead(${this.project})`); // eslint-disable-line no-console
    const result = await API.getLead(this.project);

    if (result instanceof Error) {
      this.context.commit('unsetUpdating');
      this.context.dispatch('unselectLead');

      return Promise.reject(result);
    }

    this.context.commit('setLead', { lead: result });

    // Aggiorna l'elenco dei progetti figli associati
    await this.context.dispatch('updateChildrenList');

    // Avvia il recupero differito di eventuali progetti figli
    this.context.dispatch('getChildren');

    this.context.commit('unsetDirty');
    this.context.commit('unsetUpdating');

    return Promise.resolve(result);
  }

  @Mutation
  setLeadsPool(pool: ProjectsPool) {
    console.log('mutations:setLeadsPool'); // eslint-disable-line no-console

    this.pool = pool;
  }

  @Mutation
  setInfoPool(infoPool: ProjectsInfoPool) {
    console.log('mutations:setInfoPool'); // eslint-disable-line no-console

    this.infoPool = infoPool;
  }

  @Mutation
  setChildren(children: number[]) {
    console.log('mutations:setChildren'); // eslint-disable-line no-console

    this.projectChildren = children;
  }

  @Mutation
  setLeadsPoolUpdating(poolUpdating: {
    [key: number]: boolean;
  }) {
    console.log('mutations:setLeadsPoolUpdating'); // eslint-disable-line no-console

    this.poolUpdating = poolUpdating;
  }

  @Mutation
  setLeadsPoolDirty(poolDirty: {
    [key: number]: boolean;
  }) {
    console.log('mutations:setLeadsPoolDirty'); // eslint-disable-line no-console

    this.poolDirty = poolDirty;
  }

  @Action({ rawError: true }) // rev 1
  unselectLead() {
    console.log('actions:unselectLead'); // eslint-disable-line no-console

    this.context.commit('unsetLead');
    this.context.commit('unsetDirty');

    this.context.commit('setLeadsPool', {});
    this.context.commit('setInfoPool', {});
    this.context.commit('setChildren', []);
    this.context.commit('setLeadsPoolUpdating', {});
    this.context.commit('setLeadsPoolDirty', {});
  }

  @Action({ rawError: true }) // rev 1
  async updateLead({
    updateType,
    update,
  }: {
    updateType: LeadUpdateType;
    update: any;
  }) {
    console.log('actions:updateLead'); // eslint-disable-line no-console
    console.log('actions:updateLead updateType =', updateType); // eslint-disable-line no-console
    console.log('actions:updateLead update =', update); // eslint-disable-line no-console

    // Se non c'è un progetto selezionato si ferma
    if (!this.project) return Promise.reject();

    this.context.commit('setDirty');
    this.context.commit('setUpdating');

    let result: any = null;

    switch (updateType) {
      case 'customer':
        result = await API.updateLeadContact(this.project, update);
        break;

      case 'details':
        result = await API.updateLeadDetails(this.project, update);
        break;

      case 'payments': {
        const { invoiceDiscount, loan, ...data } = update;
        result = await API.updateLeadPayments(this.project, {
          invoiceDiscount: invoiceDiscount ?? null,
          loan: loan ?? null,
          ...data,
        });
        break;
      }

      case 'supply':
        result = await API.updateLeadSupply(this.project, update);
        break;

      case 'owners':
        result = await API.updateLeadOwners(this.project, update);
        break;

      case 'sharedNotes':
        result = await API.updateLeadSharedNotes(this.project, update);
        break;

      case 'privateNotes':
        result = await API.updateLeadPrivateNotes(this.project, update);
        break;

      case 'opNotes':
        result = await API.updateLeadOpNotes(this.project, update);
        break;

      case 'practiceNotes':
        result = await API.updateLeadPracticeNotes(this.project, update);
        break;

      case 'task':
      case 'taskC':
        result = await API.updateLeadTask(update.task.LeadId
          || this.project, update.task.id, update);
        break;

      case 'attachments':
        result = await API.updateLeadAttachments('leadId' in update ? update.leadId : this.project, update);
        break;

      default:
        result = new Error(`updateType non riconosciuto (${updateType})`);
        break;
    }

    this.context.dispatch('refreshLead');

    if (result instanceof Error) {
      this.context.commit('unsetUpdating');

      return Promise.reject(result);
    }

    return result;
  }

  @Action({ rawError: true }) // rev 1
  async removeUnassignedChild({ childCount }: { childCount: number }) {
    console.log('actions:removeUnassignedChild'); // eslint-disable-line no-console

    // Se non c'è un progetto selezionato si ferma
    if (!this.project) return Promise.reject();

    this.context.commit('setDirty');
    this.context.commit('setUpdating');

    const result = await API.removeChildLead(this.project, {
      count: childCount,
    });

    this.context.dispatch('refreshLead');

    if (result instanceof Error) {
      this.context.commit('unsetUpdating');

      return Promise.reject(result);
    }

    return result;
  }

  @Action({ rawError: true }) // rev 1
  async addChild({ childType, childName }: { childType: LeadType; childName: string }) {
    console.log('actions:addChild'); // eslint-disable-line no-console

    // Se non c'è un progetto selezionato si ferma
    if (!this.project) return Promise.reject();

    this.context.commit('setDirty');
    this.context.commit('setUpdating');

    const result = await API.addChildLead(this.project, {
      type: childType,
      name: childName,
    });

    this.context.dispatch('refreshLead');

    if (result instanceof Error) {
      this.context.commit('unsetUpdating');

      return Promise.reject(result);
    }

    return result;
  }

  @Action({ rawError: true })
  async createChild({
    type, name, requester, owners, categories, tags, attachments, installerAssignees,
  }: {
    type: LeadType;
    name: string;
    requester: { id: number };
    owners: number[];
    categories: number[];
    tags: number[];
    attachments: number[];
    installerAssignees: AssignmentAssignee[];
  }) {
    console.log('actions:createChild'); // eslint-disable-line no-console

    if (!this.project) return Promise.reject();

    this.context.commit('setDirty');
    this.context.commit('setUpdating');

    const result = await API.createChildLead(this.project, {
      type, name, requester, owners, categories, tags, attachments, installerAssignees,
    });

    this.context.dispatch('refreshLead');

    if (result instanceof Error) {
      this.context.commit('unsetUpdating');
      return Promise.reject(result);
    }

    return result;
  }

  @Action({ rawError: true }) // rev 1
  async renameChild({ childCount, childName }: { childCount: number; childName: string }) {
    console.log('actions:renameChild'); // eslint-disable-line no-console

    // Se non c'è un progetto selezionato si ferma
    if (!this.project) return Promise.reject();

    this.context.commit('setDirty');
    this.context.commit('setUpdating');

    const result = await API.renameChildLead(this.project, {
      count: childCount,
      name: childName,
    });

    this.context.dispatch('refreshLead');

    if (result instanceof Error) {
      this.context.commit('unsetUpdating');

      return Promise.reject(result);
    }

    return result;
  }

  @Action({ rawError: true }) // rev 1
  async convertLead(
    { version, type, extra }: { version?: number; type: LeadType; extra?: string },
  ) {
    console.log('actions:convertLead'); // eslint-disable-line no-console

    // Se non c'è un progetto selezionato si ferma
    if (!this.project) return Promise.reject();

    this.context.commit('setDirty');
    this.context.commit('setUpdating');

    const result = await API.convertLead(this.project, {
      version,
      type,
      extra,
    });

    this.context.dispatch('refreshLead');

    if (result instanceof Error) {
      this.context.commit('unsetUpdating');

      return Promise.reject(result);
    }

    return result;
  }

  @Action({ rawError: true }) // rev 1
  async convertTask(
    { task, version }: { task: Task; version?: number },
  ) {
    console.log('actions:convertTask'); // eslint-disable-line no-console

    // Se non c'è un progetto selezionato si ferma
    if (!this.project) return Promise.reject();

    const taskId = task?.id;
    if (!taskId) return Promise.reject();

    this.context.commit('setDirty');
    this.context.commit('setUpdating');

    const result = await API.convertTask(
      this.project,
      taskId,
      {
        version,
      },
    );

    this.context.dispatch('refreshLead');

    if (result instanceof Error) {
      this.context.commit('unsetUpdating');

      return Promise.reject(result);
    }

    return result;
  }

  @Action({ rawError: true }) // rev 1
  async reopenLead() {
    console.log('actions:reopenLead'); // eslint-disable-line no-console

    // Se non c'è un progetto selezionato si ferma
    if (!this.project) return Promise.reject();

    this.context.commit('setDirty');
    this.context.commit('setUpdating');

    const result = await API.reopenLead(this.project);

    this.context.dispatch('refreshLead');

    if (result instanceof Error) {
      this.context.commit('unsetUpdating');

      return Promise.reject(result);
    }

    return result;
  }

  @Action({ rawError: true }) // rev 1
  async eraseLead(eraseChildren = true) {
    console.log('actions:eraseLead'); // eslint-disable-line no-console

    // Se non c'è un progetto selezionato si ferma
    if (!this.project) return Promise.reject();

    this.context.commit('setDirty');
    this.context.commit('setUpdating');

    const result = await API.eraseLead(this.project, eraseChildren);

    if (result instanceof Error) {
      this.context.commit('unsetUpdating');

      return Promise.reject(result);
    }

    this.context.dispatch('unselectLead');

    return result;
  }

  @Action({ rawError: true }) // rev 1
  async closeLead({ reason, notes }: { reason: ClosedReasonType; notes: string | null }) {
    console.log('actions:closeLead'); // eslint-disable-line no-console

    // Se non c'è un progetto selezionato si ferma
    if (!this.project) return Promise.reject();

    this.context.commit('setDirty');
    this.context.commit('setUpdating');

    const result = await API.closeLead(this.project, {
      closedReason: reason,
      closedNotes: notes,
    });

    this.context.dispatch('refreshLead');

    if (result instanceof Error) {
      this.context.commit('unsetUpdating');

      return Promise.reject(result);
    }

    return result;
  }

  @Action({ rawError: true }) // rev 1
  async resolveLead() {
    console.log('actions:resolveLead'); // eslint-disable-line no-console

    // Se non c'è un progetto selezionato si ferma
    if (!this.project) return Promise.reject();

    this.context.commit('setDirty');
    this.context.commit('setUpdating');

    const result = await API.resolveLead(this.project);

    this.context.dispatch('refreshLead');

    if (result instanceof Error) {
      this.context.commit('unsetUpdating');

      return Promise.reject(result);
    }

    return result;
  }

  @Action({ rawError: true }) // rev 1
  async cancelLead() {
    console.log('actions:cancelLead'); // eslint-disable-line no-console

    // Se non c'è un progetto selezionato si ferma
    if (!this.project) return Promise.reject();

    this.context.commit('setDirty');
    this.context.commit('setUpdating');

    const result = await API.cancelLead(this.project);

    this.context.dispatch('refreshLead');

    if (result instanceof Error) {
      this.context.commit('unsetUpdating');

      return Promise.reject(result);
    }

    return result;
  }

  @Action({ rawError: true }) // rev 1
  async rejectLead({ notes }: { notes: string | null }) {
    console.log('actions:rejectLead'); // eslint-disable-line no-console

    // Se non c'è un progetto selezionato si ferma
    if (!this.project) return Promise.reject();

    this.context.commit('setDirty');
    this.context.commit('setUpdating');

    const result = await API.rejectLead(this.project, {
      notes,
    });

    this.context.dispatch('refreshLead');

    if (result instanceof Error) {
      this.context.commit('unsetUpdating');

      return Promise.reject(result);
    }

    return result;
  }

  @Action({ rawError: true }) // rev 1
  async takeLead() {
    console.log('actions:takeLead'); // eslint-disable-line no-console

    // Se non c'è un progetto selezionato si ferma
    if (!this.project) return Promise.reject();

    this.context.commit('setDirty');
    this.context.commit('setUpdating');

    const result = await API.takeLead(this.project);

    this.context.dispatch('refreshLead');

    if (result instanceof Error) {
      this.context.commit('unsetUpdating');

      return Promise.reject(result);
    }

    return result;
  }

  @Action({ rawError: true }) // rev 1
  async reassignLead({ owners }: { owners: number[] }) {
    console.log('actions:reassignLead'); // eslint-disable-line no-console

    // Se non c'è un progetto selezionato si ferma
    if (!this.project) return Promise.reject();

    this.context.commit('setDirty');
    this.context.commit('setUpdating');

    const result = await API.reassignLead(this.project, {
      owners,
      groups: [],
    });

    this.context.dispatch('refreshLead');

    if (result instanceof Error) {
      this.context.commit('unsetUpdating');

      return Promise.reject(result);
    }

    return result;
  }

  @Action({ rawError: true }) // rev 1
  async relinkLead({ fatherId }: { fatherId: number }) {
    console.log('actions:relinkLead'); // eslint-disable-line no-console

    // Se non c'è un progetto selezionato si ferma
    if (!this.project) return Promise.reject();

    this.context.commit('setDirty');
    this.context.commit('setUpdating');

    const result = await API.relinkLead(this.project, {
      fatherId,
    });

    this.context.dispatch('refreshLead');

    if (result instanceof Error) {
      this.context.commit('unsetUpdating');

      return Promise.reject(result);
    }

    return result;
  }

  @Action({ rawError: true })
  async getSharedNotes({ id }: { id: number }) {
    console.log('actions:getSharedNotes'); // eslint-disable-line no-console

    const result = await API.getSharedNotes(id);

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return Promise.resolve(result);
  }

  @Action({ rawError: true })
  async getPrivateNotes({ id }: { id: number }) {
    console.log('actions:getPrivateNotes'); // eslint-disable-line no-console

    const result = await API.getPrivateNotes(id);

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return Promise.resolve(result);
  }

  @Action({ rawError: true })
  async checkDateAvailability({ id, date }: { id: number; date: Date }) {
    console.log('actions:checkDateAvailability'); // eslint-disable-line no-console

    // Converte la data in stringa
    const selectedDateString = moment(date).format('YYYY-MM-DD');

    const result: unknown = await API.checkDateAvailability(id, selectedDateString);

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return Promise.resolve((result as { count?: number }).count ?? 0);
  }

  @Action({ rawError: true })
  async checkBusyDatesForDate({
    id,
    type,
    date,
  }: {
    id: number;
    type: string;
    date: string;
  }) {
    console.log('actions:checkBusyDatesForDate'); // eslint-disable-line no-console

    const result: unknown = await API.checkBusyDatesForDate(id, type, date);

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return Promise.resolve(result);
  }

  @Action({ rawError: true })
  [SOCKET_LEAD_UPDATED](data: SocketLeadUpdatedData) {
    console.log(`store/leads actions:SOCKET_LEAD_UPDATED(${data.id}) data.action = ${data.action}`); // eslint-disable-line no-console

    // Se è stata creata una nuova lead, non effettua nessun altro controllo.
    if (data.action === 'created') return;

    // Una lead è stata aggiornata

    // Pool

    // Se la lead è nel pool...
    if (data.id in this.pool) {
      console.log(`store/leads actions:SOCKET_LEAD_UPDATED(${data.id}) la lead è nel Pool`); // eslint-disable-line no-console

      // ... verifico se la sto già aggiornanto
      if (data.id in this.poolUpdating && this.poolUpdating[data.id] === true) {
        // eslint-disable-next-line no-console
        console.log(`store/leads actions:SOCKET_LEAD_UPDATED(${data.id}) ignorato: lead in aggiornamento`);
        return;
      }

      if (data.action === 'updated') {
        // ... la aggiorno
        if (data.id !== this.project) {
          console.log(`store/leads actions:SOCKET_LEAD_UPDATED(${data.id}) aggiorno la lead NON selezionata`); // eslint-disable-line no-console

          this.context.dispatch('updateProject', { id: data.id });
        }
      } else if (data.action === 'deleted') {
        // ... la elimino
        this.context.commit('removeProjectFromPool', { id: data.id });
      } else {
        console.log(`store/leads actions:SOCKET_LEAD_UPDATED(${data.id}) azione sconosciuta (${data.action})`); // eslint-disable-line no-console
      }
    } else {
      console.log(`store/leads actions:SOCKET_LEAD_UPDATED(${data.id}) la lead non è nel pool, non è neccessario aggiornarla`); // eslint-disable-line no-console
    }

    // Selected lead

    console.log(`store/leads - actions:SOCKET_LEAD_UPDATED(${data.id}) this.project =`, this.project); // eslint-disable-line no-console
    console.log(`store/leads - actions:SOCKET_LEAD_UPDATED(${data.id}) this.projectDirty =`, this.projectDirty); // eslint-disable-line no-console
    console.log(`store/leads - actions:SOCKET_LEAD_UPDATED(${data.id}) this.projectUpdating =`, this.projectUpdating); // eslint-disable-line no-console

    // Non accetta aggiornamenti se nessuna lead è selezionata,
    // se la lead è "sporca" o sta per essere aggiornata.
    if (!this.project || this.projectDirty || this.projectUpdating) {
      return;
    }

    if (data.id === this.project) {
      // Se la lead attuale è stata aggiornata...
      if (data.action === 'updated') {
        console.log(`store/leads actions:SOCKET_LEAD_UPDATED(${data.id}) aggiorno la lead selezionata`); // eslint-disable-line no-console

        this.context.dispatch('refreshLead');
        return;
      }
      // Se la lead attuale è stata eliminata...
      if (data.action === 'deleted') {
        console.log(`store/leads actions:SOCKET_LEAD_UPDATED(${data.id}) deseleziono la lead selezionata`); // eslint-disable-line no-console

        // ... la deseleziono.
        this.context.commit('unsetLead');
      }
    } else {
      // Se è una figlia della selezionata va aggiornata

      // eslint-disable-next-line no-lonely-if
      if (this.projectChildren.includes(data.id)) {
        console.log(`store/leads actions:SOCKET_LEAD_UPDATED(${data.id}) la lead non è selezionata ma è una figlia di quella selezionata, è neccessario aggiornarla`); // eslint-disable-line no-console

        this.context.dispatch('updateProject', { id: data.id, noChildren: true });
      } else {
        console.log(`store/leads actions:SOCKET_LEAD_UPDATED(${data.id}) la lead non è selezionata e non è una figlia, non è neccessario aggiornarla`); // eslint-disable-line no-console
      }
    }
  }
}
